import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { Helmet } from 'react-helmet';
import { withRouter } from 'react-router-dom';
import { Error } from '../../components/Error';
import { TextModal } from '../../components/Modal';
import { selectErrors } from '../../modules/global/selectors';
import * as configActions from '../../modules/emptySims/actions';
import * as customerActions from '../../modules/customer/actions';
import { selectCustomerData, selectIsFetching } from '../../modules/customer/selectors';
import {
	makeSelectFetching,
	makeSelectEmptySims,
	makeSelectFreeNumbers,
	makeSelectNewSubFinished,
	makeSelectAbleToActivate
} from '../../modules/emptySims/selectors';
import { getAccountId } from '../../utils/helpers';
import ActivateSubscriptionContent from './ActivateSubscriptionContent';
import { validate } from '../../utils/validators';
import { gaB2BActivateSim } from '../../utils/analytics';
import _ from 'lodash';
import businessProducts from '../../utils/products';
import * as simActions from '../../modules/sims/actions';
import { makeSelectCurrentOrder } from '../../modules/sims/selectors';
import { ErrorCodeReason, findErrorByErrorCodeReason } from '../../utils/error';

export class ActivateSubscriptionPage extends React.PureComponent {
	// eslint-disable-line react/prefer-stateless-function
	constructor(props) {
		super(props);
		this.state = {
			value: '',
			match: false,
			foundObject: null,
			formSubmitted: false,
			modalIsOpen: false,
			modalContent: '',
			isESIM: false,
			settings: {
				voiceRoamingAllowed: { name: 'voiceRoamingAllowed', value: true },
				internationalCallsAllowed: { name: 'internationalCallsAllowed', value: true },
				roamingLimit: { name: 'roamingLimit', value: 60 },
				serviceLimitProfile: { name: 'serviceLimitProfile', value: 'P3' },
				euDataRoamingLimit: { name: 'euDataRoamingLimit', value: 2 },
				voiceMail: { name: 'voiceMail', value: false },
				unlimitedData: { name: 'unlimitedData', value: false },
				dataSuspended: { name: 'dataSuspended', value: false },
				suspendSubscriptionTemporarily: { name: 'suspendSubscriptionTemporarily', value: false },
				dataRoamingAllowed: { name: 'dataRoamingAllowed', value: true }
			},
			phoneDirectoryUserInfo: {
				firstName: '',
				lastName: '',
				streetAddress: '',
				zipCode: '',
				city: '',
				privacy: 'semi-private-number'
			}
		};
	}

	componentDidMount() {
		const { fetchEmptySims, fetchFreeNumbers, fetchCustomer, data, fetchAbleToActivate, accountType, fetchCurrentOrder } = this.props;
		const accountId = getAccountId();

		if (accountType === 'business') {
			fetchEmptySims(accountId);
			fetchFreeNumbers();
			fetchCurrentOrder(accountId);
		}
		if (!data) {
			fetchCustomer(accountId);
		}
		if (accountType === 'consumer') {
			if (this.props.location.state !== undefined && this.props.location.state.subscriptionNumber !== null) {
				fetchAbleToActivate(this.props.location.state.subscriptionNumber);
			} else {
				this.props.history.replace('/account/sims'); // eslint-disable-line
			}
		}
	}

	handleChange = (e) => {
		this.setState({
			value: e.target.value
		});
		const { emptySims } = this.props;
		const val = e.target.value;
		// Helper variable to determine if no matches found in iteration. Overwrites 'old match', if user inputs additional
		// characters after matching sim has been found.
		let noMatchesInArray = true;
		if (emptySims) {
			const arr = emptySims.toJS();
			arr.forEach((element) => {
				if (element.simId.substr(element.simId.length - 5) === val || element.simId === val) {
					noMatchesInArray = false;
					this.setState({
						match: true,
						foundObject: element
					});
				}
			});
		}
		// Resets state back to 'not found' position.
		if (noMatchesInArray) {
			this.setState({
				match: false,
				foundObject: null
			});
		}
	};

	checkPhoneDirectoryInfoValidity = (phoneDirectoryUserInfo) => {
		if (phoneDirectoryUserInfo.privacy === 'public-number') {
			return phoneDirectoryUserInfo.firstName !== '' && phoneDirectoryUserInfo.lastName !== '' && (phoneDirectoryUserInfo.zipCode === '' || validate(phoneDirectoryUserInfo.zipCode, 'postalCode'));
		}
		return true;
	};

	activateConsumerSubscription = () => {
		const { unlimitedData, ...settings } = this.state.settings; //eslint-disable-line
		const contact = {
			simName: this.props.location.state.name,
			settings: {
				...settings
			},
			phoneDirectoryUserInfo: {
				...this.state.phoneDirectoryUserInfo
			}
		};
		this.trySubmit(contact, 'consumer');
	};

	activateBusinessSubscription = (props) => {
		// spread props
		const contact = {
			simName: props.simName,
			firstName: props.firstName,
			lastName: props.lastName,
			email: props.email,
			number: props.number,
			adInfo: props.adInfo,
			costCenterName: props.costCenterName,
			costCenterAddress: props.costCenterAddress,
			simId: !this.state.isESIM ? this.state.foundObject.simId : null,
			businessId: this.props.yTunnus,
			currentOwnerProcurationEmail: props.currentOwnerProcurationEmail,
			newOwnerProcurationEmail: props.newOwnerProcurationEmail,
			emoSubscriptionType: 'B2B1',
			phoneDirectoryUserInfo: {
				...this.state.phoneDirectoryUserInfo
			},
			isESIM: this.state.isESIM,
			mnpInfo: {
				isMnp: props.isMnp,
				ordererOwnsNumber: props.ordererOwnsNumber,
				personOwner: props.personOwner,
				businessOwner: props.businessOwner,
				hasPreviousFixedTermContract: props.hasPreviousFixedTermContract,
				selectedTransferDate: props.selectedTransferDate
			},
			settings: {
				...this.state.settings
			}
		};
		this.trySubmit(contact, 'business');
	};

	trySubmit = (contact, accountType) => {
		if (this.checkPhoneDirectoryInfoValidity(this.state.phoneDirectoryUserInfo)) {
			if (accountType === 'business') {
				this.props.sendNewSub(contact);
				gaB2BActivateSim(businessProducts.getPricePerSim(this.props.currentOrder.dataPerSim, this.props.currentOrder.moisPerSim));
			} else if (accountType === 'consumer') {
				this.props.sendNewSub(contact, this.props.location.state.subscriptionNumber, accountType);
			}
			this.setState({
				formSubmitted: true
			});
			window.scrollTo(0, 0);
		} else {
			this.setState({
				modalIsOpen: true,
				modalContent: 'Tarkista numeropalveluissa näkyvät tiedot'
			});
		}
	};

	handleSettingChange = (fieldName, data) => {
		const settings = this.state.settings;
		settings[fieldName].value = data;
		this.setState({
			settings: {
				...settings
			}
		});
	};

	handlePhoneDirectoryChange = (field, value) => {
		const phoneDirectoryUserInfo = this.state.phoneDirectoryUserInfo;
		phoneDirectoryUserInfo[field] = value;
		this.setState({
			phoneDirectoryUserInfo: {
				...phoneDirectoryUserInfo
			}
		});
	};

	handleSimTypeChange = (isEsim) => {
		this.setState({
			isESIM: isEsim
		});

		if (isEsim === true) {
			this.setState({
				value: '',
				match: false,
				foundObject: null
			});
		}
	}

	closeModal = () => {
		this.setState({
			modalIsOpen: false,
			modalContent: ''
		});
	};

	getErrorCodeBasedMessage = (errors) => {
		const msisdnInUseError = findErrorByErrorCodeReason(errors, ErrorCodeReason.MSISDN_IN_USE);
		if (msisdnInUseError !== undefined) {
			return (
				msisdnInUseError.message +
				'. Jos tarkoituksesi oli siirtää Moilla jo oleva numero tälle tilille, olethan' +
				' yhteydessä asiakaspalveluumme. Jos tarvitset nykyiselle liittymällesi' +
				' uuden SIM-kortin, voit vaihtaa liittymälle uuden kortin liittymän sivulla'
			);
		}
		return undefined;
	};

	render() {
		const { data, errors, emptySims, ableToActivate, accountType, currentOrder } = this.props;
		const errorCodeBasedMessage = this.getErrorCodeBasedMessage(errors);
		const noErrors = errors.length < 1;
		const ready = data && ((accountType === 'business' && emptySims !== null && emptySims && !_.isEmpty(currentOrder)) || accountType === 'consumer');
		return (
			<article>
				<Helmet title="Ota käyttöön liittymä" meta={[{ name: 'description', content: 'Account' }]} />
				<TextModal isOpen={this.state.modalIsOpen} onRequestClose={this.closeModal} content={this.state.modalContent} />
				{noErrors && ready && (accountType !== 'consumer' || (ableToActivate !== null && ableToActivate === true)) && (
					<ActivateSubscriptionContent
						{...this.props}
						{...this.state}
						activateConsumerSubscription={this.activateConsumerSubscription}
						activateBusinessSubscription={this.activateBusinessSubscription}
						phoneDirectoryUserInfo={this.state.phoneDirectoryUserInfo}
						handlePhoneDirectoryChange={this.handlePhoneDirectoryChange}
						handleChange={this.handleChange}
						handleSettingChange={this.handleSettingChange}
						handleSimTypeChange={this.handleSimTypeChange}
					/>
				)}
				{noErrors && ready && accountType === 'consumer' && ableToActivate !== null && ableToActivate === false && (
					<Error message={'Aktivointi ei ole mahdollista. Olet ehkä jo yrittänyt aktivointia ja prosessi on kesken. Liittymän tilanne päivittyy Simmit sivulle.'} />
				)}
				{errorCodeBasedMessage !== undefined && <Error message={errorCodeBasedMessage} />}
				{!noErrors && errorCodeBasedMessage === undefined && <Error message={'Jotain meni vikaan. Yritä myöhemmin uudestaan tai ota yhteyttä asiakaspalveluumme'} />}
			</article>
		);
	}
}

ActivateSubscriptionPage.propTypes = {
	errors: PropTypes.array,
	emptySims: PropTypes.object,
	fetchEmptySims: PropTypes.func.isRequired,
	fetchFreeNumbers: PropTypes.func.isRequired,
	sendNewSub: PropTypes.func.isRequired,
	data: PropTypes.object,
	fetchCustomer: PropTypes.func,
	yTunnus: PropTypes.string,
	location: PropTypes.object,
	fetchAbleToActivate: PropTypes.func,
	ableToActivate: PropTypes.bool,
	accountType: PropTypes.string,
	offeringType: PropTypes.string,
	fetchCurrentOrder: PropTypes.func.isRequired
};

const mapStateToProps = createStructuredSelector({
	errors: selectErrors,
	fetching: makeSelectFetching(),
	emptySims: makeSelectEmptySims(),
	freeNumbers: makeSelectFreeNumbers(),
	newSubFinished: makeSelectNewSubFinished(),
	data: selectCustomerData,
	isFetching: selectIsFetching,
	ableToActivate: makeSelectAbleToActivate(),
	currentOrder: makeSelectCurrentOrder()
});

const mapDispatchToProps = (dispatch) => ({
	fetchEmptySims: (id) => dispatch(configActions.fetchEmptySims(id)),
	fetchFreeNumbers: () => dispatch(configActions.fetchFreeNumbers()),
	fetchCustomer: (id) => dispatch(customerActions.fetchCustomerData(id)),
	sendNewSub: (data, subscscriptionId, accountType) => dispatch(configActions.sendNewSub(data, subscscriptionId, accountType)),
	fetchAbleToActivate: (subscriptionNumber) => dispatch(configActions.fetchAbleToActivate(subscriptionNumber)),
	fetchCurrentOrder: (id) => dispatch(simActions.fetchCurrentOrder(id))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ActivateSubscriptionPage));
